import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Divider,
  Card,
  CardContent,
  Alert,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  styled,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Switch,
  FormControlLabel,
  MenuItem
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as RestartAltIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Palette as PaletteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Tab as TabIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Code as CodeIcon,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Link as LinkIcon,
  Image as ImageIcon,
  TableChart,
  FormatColorText,
  FormatColorFill,
  Undo,
  Redo,
  FormatSize,
  FormatQuote,
  CloudUpload,
  InsertLink,
  AspectRatio,
  PhotoSizeSelectActual,
  History,
  AddPhotoAlternate,
  // Ek İkonlar - Temel Set
  AccountBalance,
  AccountBox,
  AccountCircle,
  Analytics,
  Architecture,
  Assignment,
  AssignmentTurnedIn,
  AutoGraph,
  Build,
  BuildCircle,
  Business,
  BusinessCenter,
  Campaign,
  Category,
  Computer,
  Construction,
  ContactMail,
  CorporateFare,
  Dashboard,
  DesignServices,
  Diamond,
  Engineering,
  Favorite,
  Group,
  Handyman,
  Hardware,
  HealthAndSafety,
  Home,
  Insights,
  Inventory,
  Language,
  Launch,
  Lightbulb,
  LocalShipping,
  ManageAccounts,
  MiscellaneousServices,
  Psychology,
  RocketLaunch,
  Science,
  Security,
  Settings,
  Shield,
  Speed,
  Support,
  TrendingUp,
  Verified,
  Work,
  Workspaces,
  Search as SearchIcon,
  Star,
  EmojiEvents
} from '@mui/icons-material';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { 
  getServiceSettings, 
  saveServiceSettings,
  getAboutTabs,
  saveAboutTab,
  deleteAboutTab
} from '../../../services/ecommerceApi';
import PagePickerDialog from './PagePickerDialog';
import type { CorporateHeroSectionSettings } from '../../../types/corporateHero';
import { defaultCorporateHeroSectionSettings } from '../../../types/corporateHero';
import { buildCorporateHeroSavePayload, normalizeCorporateHeroSection } from '../../../utils/corporateHeroSettings';
import SectionVisibilityToggle from './shared/SectionVisibilityToggle';
import {
  mergeServiceCtaSettings,
  parseSectionSettingsJson,
  resolveAboutCtaSection,
  resolveSectionSettingsFromSource,
  serializeSectionSettingsJson,
  type AboutCtaSectionExtras,
  type SectionSettingsPayload,
} from '../utils/corporateSectionSettings';
import CorporateCtaSettingsPanel from './CorporateCtaSettingsPanel';
import CorporateHeroSettingsPanel from './corporateHero/CorporateHeroSettingsPanel';
import { SortableCustomTabSections } from './customTabs/SortableCustomTabSections';
import { parseTabSections } from './customTabs/parseTabSections';
import HTMLEditor from './HTMLEditor';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import SortableSubTabs from './SortableSubTabs';
import GuideButton from '../../common/guide/GuideButton';
import { buildHeroSectionOptions } from '../../../utils/corporateHeroSectionOptions';

// İkon Kütüphanesi
const ICON_LIBRARY = [
  { name: 'users', icon: Group, category: 'People' },
  { name: 'target', icon: Star, category: 'Goals' },
  { name: 'award', icon: EmojiEvents, category: 'Awards' },
  { name: 'shield', icon: Shield, category: 'Security' },
  { name: 'truck', icon: LocalShipping, category: 'Transportation' },
  { name: 'heart', icon: Favorite, category: 'Emotions' },
  { name: 'business', icon: Business, category: 'Business' },
  { name: 'analytics', icon: Analytics, category: 'Analytics' },
  { name: 'architecture', icon: Architecture, category: 'Architecture' },
  { name: 'assignment', icon: Assignment, category: 'Tasks' },
  { name: 'build', icon: Build, category: 'Build' },
  { name: 'campaign', icon: Campaign, category: 'Campaign' },
  { name: 'category', icon: Category, category: 'Category' },
  { name: 'computer', icon: Computer, category: 'Technology' },
  { name: 'construction', icon: Construction, category: 'Construction' },
  { name: 'contact', icon: ContactMail, category: 'Communication' },
  { name: 'corporate', icon: CorporateFare, category: 'Corporate' },
  { name: 'dashboard', icon: Dashboard, category: 'Panel' },
  { name: 'design', icon: DesignServices, category: 'Design' },
  { name: 'diamond', icon: Diamond, category: 'Premium' },
  { name: 'engineering', icon: Engineering, category: 'Engineering' },
  { name: 'handyman', icon: Handyman, category: 'Repair' },
  { name: 'hardware', icon: Hardware, category: 'Hardware' },
  { name: 'health', icon: HealthAndSafety, category: 'Health' },
  { name: 'home', icon: Home, category: 'Home' },
  { name: 'insights', icon: Insights, category: 'Insights' },
  { name: 'inventory', icon: Inventory, category: 'Inventory' },
  { name: 'language', icon: Language, category: 'Language' },
  { name: 'launch', icon: Launch, category: 'Launch' },
  { name: 'lightbulb', icon: Lightbulb, category: 'Ideas' },
  { name: 'manage', icon: ManageAccounts, category: 'Management' },
  { name: 'services', icon: MiscellaneousServices, category: 'Services' },
  { name: 'psychology', icon: Psychology, category: 'Psychology' },
  { name: 'rocket', icon: RocketLaunch, category: 'Rocket' },
  { name: 'science', icon: Science, category: 'Science' },
  { name: 'security', icon: Security, category: 'Security' },
  { name: 'settings', icon: Settings, category: 'Settings' },
  { name: 'speed', icon: Speed, category: 'Speed' },
  { name: 'support', icon: Support, category: 'Support' },
  { name: 'trending', icon: TrendingUp, category: 'Trend' },
  { name: 'verified', icon: Verified, category: 'Verified' },
  { name: 'work', icon: Work, category: 'Work' },
  { name: 'workspaces', icon: Workspaces, category: 'Workspaces' },
  { name: 'account-balance', icon: AccountBalance, category: 'Finance' },
  { name: 'account-box', icon: AccountBox, category: 'Account' },
  { name: 'account-circle', icon: AccountCircle, category: 'Profile' },
  { name: 'auto-graph', icon: AutoGraph, category: 'Chart' },
  { name: 'build-circle', icon: BuildCircle, category: 'Build' },
  { name: 'business-center', icon: BusinessCenter, category: 'Business Center' }
];

// İkon Seçici Komponenti
interface IconSelectorProps {
  selectedIcon: string;
  onIconSelect: (iconName: string) => void;
  open: boolean;
  onClose: () => void;
}

const IconSelector: React.FC<IconSelectorProps> = ({ selectedIcon, onIconSelect, open, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', ...Array.from(new Set(ICON_LIBRARY.map(icon => icon.category)))];
  
  const filteredIcons = ICON_LIBRARY.filter(icon => {
    const matchesSearch = icon.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         icon.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || icon.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleIconSelect = (iconName: string) => {
    onIconSelect(iconName);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon />
          Select Icon
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Arama ve Kategori Filtreleri */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search icon..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                size="small"
                label="Category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* İkon Grid */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
          gap: 1,
          maxHeight: 400,
          overflow: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 2
        }}>
          {filteredIcons.map((iconData) => {
            const IconComponent = iconData.icon;
            const isSelected = selectedIcon === iconData.name;
            
            return (
              <Tooltip key={iconData.name} title={`${iconData.name} (${iconData.category})`}>
                <Box
                  onClick={() => handleIconSelect(iconData.name)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 1.5,
                    border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  <IconComponent 
                    sx={{ 
                      fontSize: 24, 
                      color: isSelected ? '#1976d2' : '#666',
                      mb: 0.5
                    }} 
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '10px',
                      textAlign: 'center',
                      color: isSelected ? '#1976d2' : '#666'
                    }}
                  >
                    {iconData.name}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {filteredIcons.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No icons matched your search criteria.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
          {filteredIcons.length} icons found
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

// Yeni Sekme Input Component
interface NewTabInputProps {
  onAddTab: (tabName: string) => void;
  onCancel: () => void;
}

const NewTabInput: React.FC<NewTabInputProps> = ({ onAddTab, onCancel }) => {
  const { t } = useTranslation('salesSystem');
  const [tabName, setTabName] = useState('');

  const handleSubmit = () => {
    if (tabName.trim()) {
      onAddTab(tabName.trim());
      setTabName('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Box>
      <TextField
        autoFocus
        margin="dense"
        label={t('ecommerceServiceSettings.newTabInput.tabName')}
        fullWidth
        variant="outlined"
        value={tabName}
        onChange={(e) => setTabName(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={t('ecommerceServiceSettings.newTabInput.placeholder')}
      />
      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button onClick={onCancel}>{t('ecommerceServiceSettings.newTabInput.cancel')}</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={!tabName.trim()}
        >
          {t('ecommerceServiceSettings.newTabInput.add')}
        </Button>
      </DialogActions>
    </Box>
  );
};

// NOT: HTML editörü artık paylaşılan ./HTMLEditor bileşenine taşındı (resim
// ekleme/donma sorunları orada kalıcı olarak düzeltildi). Aşağıdaki eski kopya
// KULLANILMIYOR; JSX içindeki <HTMLEditor> yukarıda import edilen paylaşılan
// sürümü kullanır. Bu eski blok ileride tamamen silinebilir.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface LegacyHTMLEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  companyId?: string | null; // Firebase Storage için
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LegacyHTMLEditor: React.FC<LegacyHTMLEditorProps> = ({ value, onChange, placeholder, disabled, companyId }) => {
  const [isCodeView, setIsCodeView] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showFontSizePicker, setShowFontSizePicker] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUploadMode, setImageUploadMode] = useState<'url' | 'upload'>('url');
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [imageResizeDialogOpen, setImageResizeDialogOpen] = useState(false);
  const [imageWidth, setImageWidth] = useState<string>('');
  const [imageHeight, setImageHeight] = useState<string>('');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [originalAspectRatio, setOriginalAspectRatio] = useState<number>(1);
  const [uploadedImages, setUploadedImages] = useState<Array<{url: string, name: string, uploadDate: string}>>([]);
  const [showUploadedImages, setShowUploadedImages] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  
  const editorRef = React.useRef<HTMLDivElement>(null);
  const colorPickerRef = React.useRef<HTMLDivElement>(null);
  const bgColorPickerRef = React.useRef<HTMLDivElement>(null);
  const fontSizePickerRef = React.useRef<HTMLDivElement>(null);
  const isUserTyping = React.useRef(false); // ✅ Kullanıcı yazıyor mu kontrolü

  const colors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#FF0000', '#FF6600', '#FFCC00', '#FFFF00', '#CCFF00', '#66FF00',
    '#00FF00', '#00FF66', '#00FFCC', '#00FFFF', '#00CCFF', '#0066FF',
    '#0000FF', '#6600FF', '#CC00FF', '#FF00FF', '#FF00CC', '#FF0066'
  ];

  const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72];

  const handleCommand = (command: string, value?: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, value);
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      isUserTyping.current = true; // ✅ Kullanıcı yazıyor
      onChange(editorRef.current.innerHTML);
    }
  };

  // ✅ Value dışarıdan değiştiğinde (prop olarak) editörü güncelle
  React.useEffect(() => {
    if (editorRef.current && !isUserTyping.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
    isUserTyping.current = false; // Reset flag
  }, [value]);

  // Gelişmiş içerik hizalama
  const handleContentAlignment = (alignment: 'left' | 'center' | 'right') => {
    if (!editorRef.current) return;

    // Seçili metni al
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    
    // Eğer hiçbir şey seçili değilse, tüm editör içeriğini hizala
    if (selection.toString().trim() === '') {
      // Tüm editör içeriğini div ile sar ve hizala
      const alignmentStyle = alignment === 'left' ? 'text-align: left;' : 
                           alignment === 'center' ? 'text-align: center;' : 
                           'text-align: right;';
      
      const currentContent = editorRef.current.innerHTML;
      const alignedContent = `<div style="${alignmentStyle}">${currentContent}</div>`;
      
      editorRef.current.innerHTML = alignedContent;
      onChange(editorRef.current.innerHTML);
      return;
    }

    // Seçili içeriği hizala
    try {
      // Seçili içeriği div ile sar
      const selectedContent = range.extractContents();
      const div = document.createElement('div');
      
      // Hizalama stilini ekle
      switch (alignment) {
        case 'left':
          div.style.textAlign = 'left';
          break;
        case 'center':
          div.style.textAlign = 'center';
          break;
        case 'right':
          div.style.textAlign = 'right';
          break;
      }
      
      div.appendChild(selectedContent);
      range.insertNode(div);
      
      // Seçimi temizle
      selection.removeAllRanges();
      
      // İçeriği güncelle
      onChange(editorRef.current.innerHTML);
    } catch (error) {
      console.error('Alignment error:', error);
      // Fallback: Standart hizalama komutlarını kullan
      const command = alignment === 'left' ? 'justifyLeft' : 
                     alignment === 'center' ? 'justifyCenter' : 
                     'justifyRight';
      handleCommand(command);
    }
  };

  // Resim seçme ve boyutlandırma
  const handleImageClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      setSelectedImage(img);
      
      // Mevcut boyutları al
      const currentWidth = img.style.width || img.width + 'px';
      const currentHeight = img.style.height || img.height + 'px';
      
      setImageWidth(currentWidth.replace('px', ''));
      setImageHeight(currentHeight.replace('px', ''));
      
      // Orijinal aspect ratio'yu hesapla
      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;
      setOriginalAspectRatio(naturalWidth / naturalHeight);
      
      setImageResizeDialogOpen(true);
    }
  };

  const applyImageResize = () => {
    if (!selectedImage) return;

    const widthValue = imageWidth ? `${imageWidth}px` : 'auto';
    const heightValue = imageHeight ? `${imageHeight}px` : 'auto';

    selectedImage.style.width = widthValue;
    selectedImage.style.height = heightValue;
    
    // Editör içeriğini güncelle
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }

    // Dialog'u kapat
    setImageResizeDialogOpen(false);
    setSelectedImage(null);
  };

  const handleWidthChange = (newWidth: string) => {
    setImageWidth(newWidth);
    
    if (maintainAspectRatio && newWidth && originalAspectRatio) {
      const calculatedHeight = Math.round(parseInt(newWidth) / originalAspectRatio);
      setImageHeight(calculatedHeight.toString());
    }
  };

  const handleHeightChange = (newHeight: string) => {
    setImageHeight(newHeight);
    
    if (maintainAspectRatio && newHeight && originalAspectRatio) {
      const calculatedWidth = Math.round(parseInt(newHeight) * originalAspectRatio);
      setImageWidth(calculatedWidth.toString());
    }
  };

  const resetImageSize = () => {
    if (!selectedImage) return;
    
    const naturalWidth = selectedImage.naturalWidth;
    const naturalHeight = selectedImage.naturalHeight;
    
    setImageWidth(naturalWidth.toString());
    setImageHeight(naturalHeight.toString());
  };

  const setImageSizePreset = (preset: 'small' | 'medium' | 'large' | 'full') => {
    if (!selectedImage) return;
    
    const naturalWidth = selectedImage.naturalWidth;
    const naturalHeight = selectedImage.naturalHeight;
    
    let newWidth: number;
    
    switch (preset) {
      case 'small':
        newWidth = Math.min(200, naturalWidth);
        break;
      case 'medium':
        newWidth = Math.min(400, naturalWidth);
        break;
      case 'large':
        newWidth = Math.min(600, naturalWidth);
        break;
      case 'full':
        newWidth = naturalWidth;
        break;
      default:
        newWidth = naturalWidth;
    }
    
    const newHeight = Math.round(newWidth / originalAspectRatio);
    
    setImageWidth(newWidth.toString());
    setImageHeight(newHeight.toString());
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  const insertLink = () => {
    if (linkUrl && linkText) {
      const linkHtml = `<a href="${linkUrl}" target="_blank">${linkText}</a>`;
      handleCommand('insertHTML', linkHtml);
      setLinkUrl('');
      setLinkText('');
      setLinkDialogOpen(false);
    }
  };

  const insertImage = async () => {
    try {
      setImageUploading(true);
      let imagesHtml = '';

      // URL modu
      if (imageUploadMode === 'url' && imageUrl) {
        imagesHtml = `<img src="${imageUrl}" alt="${imageAlt}" style="max-width: 100%; height: auto; margin: 8px;" />`;
      }
      
      // Dosya yükleme modu - birden fazla dosya
      else if (imageUploadMode === 'upload' && imageFiles.length > 0 && companyId) {
        const uploadPromises = imageFiles.map(async (file, index) => {
          try {
            // Progress tracking için unique key
            const progressKey = `${file.name}-${index}`;
            setUploadProgress(prev => ({ ...prev, [progressKey]: 0 }));
            
            // Dosya adını oluştur
            const fileExtension = file.name.split('.').pop();
            const fileName = `about-content-${Date.now()}-${index}-${uuidv4()}.${fileExtension}`;
            
            // Firebase Storage referansı
            const imageRef = storageRef(storage, `companies/${companyId}/about-images/${fileName}`);
            
            // Dosyayı yükle
            await uploadBytes(imageRef, file);
            
            // Progress güncelle
            setUploadProgress(prev => ({ ...prev, [progressKey]: 100 }));
            
            // Download URL'ini al
            const downloadURL = await getDownloadURL(imageRef);
            
            // Yüklenen resmi listeye ekle
            const uploadedImage = {
              url: downloadURL,
              name: file.name,
              uploadDate: new Date().toISOString()
            };
            
            setUploadedImages(prev => {
              const exists = prev.some(img => img.url === downloadURL);
              if (!exists) {
                return [...prev, uploadedImage];
              }
              return prev;
            });
            
            return `<img src="${downloadURL}" alt="${imageAlt || file.name}" style="max-width: 100%; height: auto; margin: 8px;" />`;
          } catch (error) {
            console.error(`Error while uploading ${file.name}:`, error);
            return `<p style="color: red;">❌ ${file.name} could not be uploaded</p>`;
          }
        });

        const uploadedImages = await Promise.all(uploadPromises);
        imagesHtml = uploadedImages.join('\n');
      }

      if (imagesHtml) {
        handleCommand('insertHTML', imagesHtml);
        
        // Form'u temizle
        setImageUrl('');
        setImageAlt('');
        setImageFiles([]);
        setImageUploadMode('url');
        setUploadProgress({});
        setImageDialogOpen(false);
      }
    } catch (error) {
      console.error('Error while uploading images:', error);
      alert('An error occurred while uploading images. Please try again.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach((file, index) => {
      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: File size must be smaller than 5MB.`);
        return;
      }
      
      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Only image files are accepted.`);
        return;
      }

      validFiles.push(file);
    });

    // Hataları göster
    if (errors.length > 0) {
      alert('Some files could not be uploaded:\n\n' + errors.join('\n'));
    }

    // Geçerli dosyaları ekle (mevcut dosyalarla birleştir)
    if (validFiles.length > 0) {
      setImageFiles(prev => [...prev, ...validFiles]);
    }

    // Input'u temizle (aynı dosyayı tekrar seçebilmek için)
    event.target.value = '';
  };

  const removeImageFile = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllImageFiles = () => {
    setImageFiles([]);
    setUploadProgress({});
  };

  const insertUploadedImage = (imageUrl: string, imageName: string) => {
    const imgHtml = `<img src="${imageUrl}" alt="${imageAlt || imageName}" style="max-width: 100%; height: auto; margin: 8px;" />`;
    handleCommand('insertHTML', imgHtml);
    
    // Modal'ı kapat
    setImageUrl('');
    setImageAlt('');
    setImageFiles([]);
    setImageUploadMode('url');
    setUploadProgress({});
    setImageDialogOpen(false);
  };

  const deleteUploadedImage = (imageUrl: string) => {
    setUploadedImages(prev => prev.filter(img => img.url !== imageUrl));
  };

  const insertTable = () => {
    let tableHtml = '<table border="1" style="border-collapse: collapse; width: 100%;">';
    for (let i = 0; i < tableRows; i++) {
      tableHtml += '<tr>';
      for (let j = 0; j < tableCols; j++) {
        tableHtml += '<td style="padding: 8px; border: 1px solid #ddd;">Cell</td>';
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table>';
    handleCommand('insertHTML', tableHtml);
    setTableDialogOpen(false);
  };

  const handleColorSelect = (color: string) => {
    if (!editorRef.current) return;
    
    // Seçili metni al
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // Hiçbir şey seçili değilse, editörün tamamına uygula
      editorRef.current.style.color = color;
    } else {
      // Seçili metin varsa, sadece o kısma uygula
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        // İmleç bir yerdeyse, yeni yazılacak metne uygula
        document.execCommand('foreColor', false, color);
      } else {
        // Metin seçiliyse, span ile sarala
        const span = document.createElement('span');
        span.style.color = color;
        try {
          range.surroundContents(span);
        } catch (e) {
          // Karmaşık seçimler için alternatif yöntem
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      }
    }
    
    onChange(editorRef.current.innerHTML);
    setShowColorPicker(false);
  };

  const handleBgColorSelect = (color: string) => {
    if (!editorRef.current) return;
    
    // Seçili metni al
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // Hiçbir şey seçili değilse, editörün tamamına uygula
      editorRef.current.style.backgroundColor = color;
    } else {
      // Seçili metin varsa, sadece o kısma uygula
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        // İmleç bir yerdeyse, yeni yazılacak metne uygula
        document.execCommand('backColor', false, color);
      } else {
        // Metin seçiliyse, span ile sarala
        const span = document.createElement('span');
        span.style.backgroundColor = color;
        try {
          range.surroundContents(span);
        } catch (e) {
          // Karmaşık seçimler için alternatif yöntem
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      }
    }
    
    onChange(editorRef.current.innerHTML);
    setShowBgColorPicker(false);
  };

  const handleFontSizeSelect = (size: number) => {
    if (!editorRef.current) return;
    
    // Seçili metni al
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // Hiçbir şey seçili değilse, editörün tamamına uygula
      editorRef.current.style.fontSize = `${size}px`;
    } else {
      // Seçili metin varsa, sadece o kısma uygula
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        // İmleç bir yerdeyse, yeni yazılacak metne uygula
        document.execCommand('fontSize', false, '7'); // Geçici değer
        const fontElements = editorRef.current.querySelectorAll('font[size="7"]');
        fontElements.forEach(el => {
          el.removeAttribute('size');
          (el as HTMLElement).style.fontSize = `${size}px`;
        });
      } else {
        // Metin seçiliyse, span ile sarala
        const span = document.createElement('span');
        span.style.fontSize = `${size}px`;
        try {
          range.surroundContents(span);
        } catch (e) {
          // Karmaşık seçimler için alternatif yöntem
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
        selection.removeAllRanges();
      }
    }
    
    onChange(editorRef.current.innerHTML);
    setShowFontSizePicker(false);
  };

  // Click outside handlers
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false);
      }
      if (bgColorPickerRef.current && !bgColorPickerRef.current.contains(event.target as Node)) {
        setShowBgColorPicker(false);
      }
      if (fontSizePickerRef.current && !fontSizePickerRef.current.contains(event.target as Node)) {
        setShowFontSizePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
      {/* Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        p: 1, 
        borderBottom: '1px solid #ddd', 
        backgroundColor: '#f8f9fa',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Geri Al / İleri Al */}
        <Tooltip title="Undo">
          <IconButton size="small" onClick={() => handleCommand('undo')} disabled={disabled || isCodeView}>
            <Undo />
          </IconButton>
        </Tooltip>
        <Tooltip title="Redo">
          <IconButton size="small" onClick={() => handleCommand('redo')} disabled={disabled || isCodeView}>
            <Redo />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Font Boyutu */}
        <Box sx={{ position: 'relative' }} ref={fontSizePickerRef}>
          <Tooltip title="Font Size">
            <IconButton 
              size="small" 
              onClick={() => setShowFontSizePicker(!showFontSizePicker)}
              disabled={disabled || isCodeView}
            >
              <FormatSize />
            </IconButton>
          </Tooltip>
          {showFontSizePicker && (
            <Paper sx={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              zIndex: 1000,
              maxHeight: 200,
              overflow: 'auto',
              minWidth: 80
            }}>
              {fontSizes.map(size => (
                <Button
                  key={size}
                  fullWidth
                  size="small"
                  onClick={() => handleFontSizeSelect(size)}
                  sx={{ justifyContent: 'flex-start', fontSize: `${Math.min(size, 16)}px` }}
                >
                  {size}px
                </Button>
              ))}
            </Paper>
          )}
        </Box>

        {/* Metin Formatı */}
        <Tooltip title="Bold">
          <IconButton size="small" onClick={() => handleCommand('bold')} disabled={disabled || isCodeView}>
            <FormatBold />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton size="small" onClick={() => handleCommand('italic')} disabled={disabled || isCodeView}>
            <FormatItalic />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline">
          <IconButton size="small" onClick={() => handleCommand('underline')} disabled={disabled || isCodeView}>
            <FormatUnderlined />
          </IconButton>
        </Tooltip>

        {/* Renk Seçiciler */}
        <Box sx={{ position: 'relative' }} ref={colorPickerRef}>
          <Tooltip title="Text Color">
            <IconButton 
              size="small" 
              onClick={() => setShowColorPicker(!showColorPicker)}
              disabled={disabled || isCodeView}
            >
              <FormatColorText />
            </IconButton>
          </Tooltip>
          {showColorPicker && (
            <Paper sx={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              zIndex: 1000,
              p: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 0.5,
              width: 180
            }}>
              {colors.map(color => (
                <Box
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    '&:hover': { transform: 'scale(1.1)' }
                  }}
                />
              ))}
            </Paper>
          )}
        </Box>

        <Box sx={{ position: 'relative' }} ref={bgColorPickerRef}>
          <Tooltip title="Background Color">
            <IconButton 
              size="small" 
              onClick={() => setShowBgColorPicker(!showBgColorPicker)}
              disabled={disabled || isCodeView}
            >
              <FormatColorFill />
            </IconButton>
          </Tooltip>
          {showBgColorPicker && (
            <Paper sx={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              zIndex: 1000,
              p: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 0.5,
              width: 180
            }}>
              {colors.map(color => (
                <Box
                  key={color}
                  onClick={() => handleBgColorSelect(color)}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    '&:hover': { transform: 'scale(1.1)' }
                  }}
                />
              ))}
            </Paper>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Hizalama - Gelişmiş */}
        <Box sx={{ display: 'flex', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Tooltip title="Align Left">
            <IconButton 
              size="small" 
              onClick={() => handleContentAlignment('left')} 
              disabled={disabled || isCodeView}
              sx={{ 
                borderRadius: 0,
                borderRight: '1px solid #ddd',
                '&:hover': { backgroundColor: '#e3f2fd' }
              }}
            >
              <FormatAlignLeft />
            </IconButton>
          </Tooltip>
          <Tooltip title="Center">
            <IconButton 
              size="small" 
              onClick={() => handleContentAlignment('center')} 
              disabled={disabled || isCodeView}
              sx={{ 
                borderRadius: 0,
                borderRight: '1px solid #ddd',
                '&:hover': { backgroundColor: '#e3f2fd' }
              }}
            >
              <FormatAlignCenter />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Right">
            <IconButton 
              size="small" 
              onClick={() => handleContentAlignment('right')} 
              disabled={disabled || isCodeView}
              sx={{ 
                borderRadius: 0,
                '&:hover': { backgroundColor: '#e3f2fd' }
              }}
            >
              <FormatAlignRight />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Ek Hizalama Seçenekleri */}
        <Tooltip title="Justify">
          <IconButton size="small" onClick={() => handleCommand('justifyFull')} disabled={disabled || isCodeView}>
            <FormatAlignJustify />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Listeler */}
        <Tooltip title="Bulleted List">
          <IconButton size="small" onClick={() => handleCommand('insertUnorderedList')} disabled={disabled || isCodeView}>
            <FormatListBulleted />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered List">
          <IconButton size="small" onClick={() => handleCommand('insertOrderedList')} disabled={disabled || isCodeView}>
            <FormatListNumbered />
          </IconButton>
        </Tooltip>

        {/* Alıntı */}
        <Tooltip title="Quote">
          <IconButton size="small" onClick={() => handleCommand('formatBlock', '<blockquote>')} disabled={disabled || isCodeView}>
            <FormatQuote />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Başlıklar */}
        <Tooltip title="Heading 1">
          <Button size="small" onClick={() => handleCommand('formatBlock', '<h1>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H1
          </Button>
        </Tooltip>
        <Tooltip title="Heading 2">
          <Button size="small" onClick={() => handleCommand('formatBlock', '<h2>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H2
          </Button>
        </Tooltip>
        <Tooltip title="Heading 3">
          <Button size="small" onClick={() => handleCommand('formatBlock', '<h3>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H3
          </Button>
        </Tooltip>
        <Tooltip title="Paragraph">
          <Button size="small" onClick={() => handleCommand('formatBlock', '<p>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            P
          </Button>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Link, Resim, Tablo */}
        <Tooltip title="Insert Link">
          <IconButton size="small" onClick={() => setLinkDialogOpen(true)} disabled={disabled || isCodeView}>
            <LinkIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert Image">
          <IconButton size="small" onClick={() => setImageDialogOpen(true)} disabled={disabled || isCodeView}>
            <ImageIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert Table">
          <IconButton size="small" onClick={() => setTableDialogOpen(true)} disabled={disabled || isCodeView}>
            <TableChart />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* HTML Kod Görünümü */}
        <Tooltip title={isCodeView ? "Visual Editor" : "HTML Code"}>
          <IconButton 
            size="small" 
            onClick={() => setIsCodeView(!isCodeView)}
            disabled={disabled}
            color={isCodeView ? "primary" : "default"}
          >
            <CodeIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Editor Content */}
      {isCodeView ? (
        <TextField
          multiline
          rows={12}
          value={value}
          onChange={handleCodeChange}
          placeholder={placeholder}
          disabled={disabled}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{ 
            '& .MuiInputBase-root': { 
              p: 2,
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: '13px',
              lineHeight: 1.4
            }
          }}
          fullWidth
        />
      ) : (
        <Box
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleContentChange}
          onClick={handleImageClick}
          suppressContentEditableWarning
          sx={{
            minHeight: '300px',
            p: 2,
            outline: 'none',
            lineHeight: 1.6,
            '&:focus': {
              backgroundColor: '#fafafa'
            },
            '&[contenteditable="false"]': {
              backgroundColor: '#f5f5f5',
              color: '#666'
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              margin: '16px 0 8px 0',
              fontWeight: 'bold'
            },
            '& p': {
              margin: '8px 0'
            },
            '& ul, & ol': {
              margin: '8px 0',
              paddingLeft: '24px'
            },
            '& blockquote': {
              margin: '16px 0',
              padding: '12px 16px',
              borderLeft: '4px solid #ddd',
              backgroundColor: '#f9f9f9',
              fontStyle: 'italic'
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              margin: '16px 0'
            },
            '& td, & th': {
              padding: '8px',
              border: '1px solid #ddd'
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              cursor: 'pointer',
              border: '2px solid transparent',
              borderRadius: '4px',
              transition: 'border-color 0.2s ease',
              '&:hover': {
                borderColor: '#1976d2',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)'
              }
            }
          }}
        />
      )}
      
      {!value && !isCodeView && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            position: 'absolute', 
            top: '80px', 
            left: '16px',
            pointerEvents: 'none',
            opacity: 0.6
          }}
        >
          {placeholder}
        </Typography>
      )}

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Ekle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Link Metni"
            fullWidth
            variant="outlined"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>İptal</Button>
          <Button onClick={insertLink} variant="contained" disabled={!linkUrl || !linkText}>
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon />
            Resim Ekle
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Mod Seçimi */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Resim Ekleme Yöntemi
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant={imageUploadMode === 'url' ? 'contained' : 'outlined'}
                startIcon={<InsertLink />}
                onClick={() => {
                  setImageUploadMode('url');
                  setShowUploadedImages(false);
                }}
                size="small"
              >
                URL ile Ekle
              </Button>
              <Button
                variant={imageUploadMode === 'upload' ? 'contained' : 'outlined'}
                startIcon={<CloudUpload />}
                onClick={() => {
                  setImageUploadMode('upload');
                  setShowUploadedImages(false);
                }}
                size="small"
                disabled={!companyId}
              >
                Dosya Yükle
              </Button>
              {uploadedImages.length > 0 && (
                <Button
                  variant={showUploadedImages ? 'contained' : 'outlined'}
                  startIcon={<History />}
                  onClick={() => {
                    setShowUploadedImages(!showUploadedImages);
                    if (!showUploadedImages) {
                      setImageUploadMode('url'); // Reset mode
                    }
                  }}
                  size="small"
                  color="secondary"
                >
                  Yüklenen Resimler ({uploadedImages.length})
                </Button>
              )}
            </Box>
            {!companyId && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                ⚠️ Dosya yükleme için şirket ID gerekli
              </Typography>
            )}
          </Box>

          {/* URL Modu */}
          {imageUploadMode === 'url' && (
            <Box>
              <TextField
                autoFocus
                margin="dense"
                label="Image URL"
                fullWidth
                variant="outlined"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                sx={{ mb: 2 }}
                helperText="Enter the image web address"
              />
            </Box>
          )}

          {/* Dosya Yükleme Modu */}
          {imageUploadMode === 'upload' && (
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload-input"
                type="file"
                multiple
                onChange={handleImageFilesChange}
              />
              <label htmlFor="image-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{ 
                    mb: 2, 
                    py: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    '&:hover': {
                      borderStyle: 'dashed',
                      borderWidth: 2,
                    }
                  }}
                >
                  {imageFiles.length > 0 
                    ? `${imageFiles.length} Images Selected - Add More` 
                    : 'Select Image Files (Multiple Selection)'}
                </Button>
              </label>
              
              {imageFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Selected Images ({imageFiles.length})
                    </Typography>
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={clearAllImageFiles}
                      startIcon={<DeleteIcon />}
                    >
                      Clear All
                    </Button>
                  </Box>
                  
                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                    {imageFiles.map((file, index) => (
                      <Box 
                        key={`${file.name}-${index}`}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1,
                          mb: 1,
                          backgroundColor: '#f9f9f9',
                          borderRadius: 1,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            📷 {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                          {uploadProgress[`${file.name}-${index}`] !== undefined && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="primary.main">
                                Uploading: %{uploadProgress[`${file.name}-${index}`]}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => removeImageFile(index)}
                          disabled={imageUploading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                • <strong>Multi selection:</strong> You can select multiple images with Ctrl/Cmd<br/>
                • <strong>Maximum file size:</strong> 5MB per file<br/>
                • <strong>Supported formats:</strong> JPG, PNG, GIF, WebP<br/>
                • <strong>Storage:</strong> All images will be uploaded to Firebase Storage
              </Typography>
            </Box>
          )}

          {/* Yüklenen Resimler Bölümü */}
          {showUploadedImages && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Previously Uploaded Images:
              </Typography>
              
              {uploadedImages.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4, 
                  border: '2px dashed #ddd', 
                  borderRadius: 1,
                  backgroundColor: '#f9f9f9'
                }}>
                  <AddPhotoAlternate sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    No images uploaded yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Your images will appear here after upload
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  maxHeight: 300, 
                  overflow: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  p: 2,
                  backgroundColor: '#fafafa'
                }}>
                  <Grid container spacing={2}>
                    {uploadedImages.map((image, index) => (
                      <Grid item xs={6} sm={4} md={3} key={`${image.url}-${index}`}>
                        <Card sx={{ position: 'relative', '&:hover': { boxShadow: 3 } }}>
                          <Box sx={{ position: 'relative', paddingTop: '75%' }}>
                            <img 
                              src={image.url} 
                              alt={image.name}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '4px 4px 0 0'
                              }}
                            />
                          </Box>
                          <CardContent sx={{ p: 1 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '11px'
                              }}
                              title={image.name}
                            >
                              {image.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                              {new Date(image.uploadDate).toLocaleDateString('tr-TR')}
                            </Typography>
                          </CardContent>
                          
                          {/* Hover Butonları */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            borderRadius: 1,
                            '&:hover': { opacity: 1 }
                          }}>
                            <Tooltip title="Insert Image">
                              <IconButton
                                size="small"
                                onClick={() => insertUploadedImage(image.url, image.name)}
                                sx={{ 
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  color: 'primary.main',
                                  '&:hover': { backgroundColor: 'white' }
                                }}
                              >
                                <AddPhotoAlternate fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete from List">
                              <IconButton
                                size="small"
                                onClick={() => deleteUploadedImage(image.url)}
                                sx={{ 
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  color: 'error.main',
                                  '&:hover': { backgroundColor: 'white' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                💡 <strong>Tip:</strong> You can re-insert images you removed from the editor here.
                Hover on the image and click the "Insert Image" button.
              </Typography>
            </Box>
          )}

          {/* Alt Metin */}
          <TextField
            margin="dense"
            label="Alt Text (Optional)"
            fullWidth
            variant="outlined"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
            placeholder="Image description"
            helperText="Recommended for SEO and accessibility"
          />

          {/* Önizleme */}
          {((imageUploadMode === 'url' && imageUrl) || (imageUploadMode === 'upload' && imageFiles.length > 0)) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview:
              </Typography>
              <Box sx={{ 
                border: '1px solid #ddd', 
                borderRadius: 1, 
                p: 2,
                backgroundColor: '#fafafa',
                maxHeight: 300,
                overflow: 'auto'
              }}>
                {imageUploadMode === 'url' && imageUrl ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <img 
                      src={imageUrl} 
                      alt={imageAlt || 'Preview'} 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain' 
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </Box>
                ) : imageFiles.length > 0 ? (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                    gap: 2 
                  }}>
                    {imageFiles.map((file, index) => (
                      <Box 
                        key={`preview-${file.name}-${index}`}
                        sx={{ 
                          position: 'relative',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden',
                          backgroundColor: '#fff'
                        }}
                      >
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={file.name} 
                          style={{ 
                            width: '100%', 
                            height: '80px', 
                            objectFit: 'cover' 
                          }}
                        />
                        <Box sx={{ p: 1 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.9)'
                            }
                          }}
                          onClick={() => removeImageFile(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setImageDialogOpen(false);
              setImageUrl('');
              setImageAlt('');
              setImageFiles([]);
              setImageUploadMode('url');
              setUploadProgress({});
              setShowUploadedImages(false);
            }}
            disabled={imageUploading}
          >
            Cancel
          </Button>
          <Button 
            onClick={insertImage} 
            variant="contained" 
            disabled={
              imageUploading || 
              (imageUploadMode === 'url' && !imageUrl) || 
              (imageUploadMode === 'upload' && imageFiles.length === 0)
            }
            startIcon={imageUploading ? <CircularProgress size={16} /> : <ImageIcon />}
          >
            {imageUploading 
              ? `Uploading... (${imageFiles.length} images)` 
              : imageUploadMode === 'upload' && imageFiles.length > 1
                ? `Insert ${imageFiles.length} Images`
                : 'Insert Image'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table Dialog */}
      <Dialog open={tableDialogOpen} onClose={() => setTableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Table</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Row Count"
                type="number"
                fullWidth
                variant="outlined"
                value={tableRows}
                onChange={(e) => setTableRows(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{ inputProps: { min: 1, max: 20 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Column Count"
                type="number"
                fullWidth
                variant="outlined"
                value={tableCols}
                onChange={(e) => setTableCols(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)}>Cancel</Button>
          <Button onClick={insertTable} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resim Boyutlandırma Dialog */}
      <Dialog 
        open={imageResizeDialogOpen} 
        onClose={() => setImageResizeDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoSizeSelectActual />
            Adjust Image Size
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box>
              {/* Mevcut Resim Önizleme */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Image:
                </Typography>
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  p: 2, 
                  backgroundColor: '#fafafa',
                  display: 'inline-block'
                }}>
                  <img 
                    src={selectedImage.src} 
                    alt={selectedImage.alt || 'Selected image'} 
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '150px', 
                      objectFit: 'contain' 
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Original Size: {selectedImage.naturalWidth} × {selectedImage.naturalHeight} px
                </Typography>
              </Box>

              {/* Hızlı Boyut Seçenekleri */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Quick Size Options:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setImageSizePreset('small')}
                  >
                    Small (200px)
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setImageSizePreset('medium')}
                  >
                    Medium (400px)
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setImageSizePreset('large')}
                  >
                    Large (600px)
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setImageSizePreset('full')}
                  >
                    Original
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="secondary"
                    onClick={resetImageSize}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>

              {/* Manuel Boyut Ayarları */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Manual Size Settings:
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label="Width (px)"
                      type="number"
                      value={imageWidth}
                      onChange={(e) => handleWidthChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        inputProps: { min: 1, max: 2000 }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={2} sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={() => setMaintainAspectRatio(!maintainAspectRatio)}
                      color={maintainAspectRatio ? 'primary' : 'default'}
                      title={maintainAspectRatio ? 'Keep ratio (On)' : 'Keep ratio (Off)'}
                    >
                      <AspectRatio />
                    </IconButton>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label="Height (px)"
                      type="number"
                      value={imageHeight}
                      onChange={(e) => handleHeightChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        inputProps: { min: 1, max: 2000 }
                      }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AspectRatio color={maintainAspectRatio ? 'primary' : 'disabled'} />
                  <Typography variant="body2" color={maintainAspectRatio ? 'primary' : 'text.secondary'}>
                    {maintainAspectRatio ? 'Aspect ratio is preserved' : 'Aspect ratio is free'}
                  </Typography>
                </Box>
              </Box>

              {/* Boyut Bilgileri */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1,
                mb: 2
              }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  📏 Size Information:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  • New Size: {imageWidth || 'auto'} × {imageHeight || 'auto'} px<br/>
                  • Original Size: {selectedImage.naturalWidth} × {selectedImage.naturalHeight} px<br/>
                  • Aspect Ratio: {originalAspectRatio.toFixed(2)}<br/>
                  • Size Change: {imageWidth && selectedImage.naturalWidth ? 
                    `%${Math.round((parseInt(imageWidth) / selectedImage.naturalWidth) * 100)}` : 
                    'Calculating...'}
                </Typography>
              </Box>

              {/* Uyarılar */}
              {imageWidth && parseInt(imageWidth) > selectedImage.naturalWidth && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠️ You are enlarging the image beyond its original size. This may cause quality loss.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setImageResizeDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={applyImageResize} 
            variant="contained"
            startIcon={<PhotoSizeSelectActual />}
            disabled={!imageWidth && !imageHeight}
          >
            Apply Size
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Styled components - ProductSettings.tsx'den kopyalandı
const StyledTabs = styled(Tabs)({
  minHeight: 'auto',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

const StyledTab = styled((props: { label: string }) => (
    <Tab disableRipple {...props} />
  ))(({ theme }) => ({
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(1, 3.5),
    minHeight: 'auto',
    minWidth: '120px',
    color: theme.palette.text.secondary,
    position: 'relative',
    backgroundColor: theme.palette.grey[200],
    clipPath: 'polygon(15% 0, 85% 0, 100% 100%, 0% 100%)',
    marginRight: theme.spacing(-2),

    '&:hover': {
        backgroundColor: theme.palette.grey[300],
    },
  
    '&.Mui-selected': {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: theme.palette.background.paper,
      zIndex: 2,
    },
}));

// Tab paneli için yardımcı bileşen
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`about-tabpanel-${index}`}
      aria-labelledby={`about-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface ServiceTabSection {
  id: string;
  title: string;
  content: string;
  image?: string;
  imagePosition?: 'left' | 'right';
  imageSize?: 'small' | 'medium' | 'large' | 'xlarge' | 'equal';
  imageWidth?: number | string;
  imageHeight?: number | string;
  imageAnimation?: 'fade' | 'zoom' | 'slide' | 'none';
}

interface ServiceTab {
  id: string;
  name: string;
  title: string;
  content: string;
  backgroundColor: string;
  order: number;
  active: boolean;
  sections?: ServiceTabSection[]; // Yeni: Dinamik bölümler
  navDisplayMode?: 'panel' | 'navbar' | 'both'; // Gösterim yeri: yan panel / üst navbar / ikisi
}

interface ServiceSubMenuItem {
  id: string;
  label: string;
  slug: string;
  url: string;
  displayOrder: number;
  isActive: boolean;
}

interface ServiceSettings {
  heroSection: CorporateHeroSectionSettings;
  storySection: {
    title: string;
    description: string;
    backgroundColor: string;
    stats: {
      customers: string;
      products: string;
      support: string;
      satisfaction: string;
    };
  };
  valuesSection: {
    title: string;
    description: string;
    backgroundColor: string;
    values: Array<{
      id: string;
      title: string;
      description: string;
      icon: string;
      color: string;
      active?: boolean;
      detailLinkEnabled?: boolean;
      detailLinkUrl?: string;
      detailLinkText?: string;
    }>;
  };
  whyChooseSection: {
    title: string;
    description: string;
    backgroundColor: string;
    features: Array<{
      id: string;
      title: string;
      description: string;
      image?: string;
    }>;
  };
  ctaSection: {
    enabled: boolean;
    title: string;
    description: string;
    backgroundColor: string;
    textColor: string;
    primaryButton: {
      enabled: boolean;
      text: string;
      action: 'scroll-to-top' | 'contact-page';
    };
    secondaryButton: {
      enabled: boolean;
      text: string;
      action: 'scroll-to-top' | 'contact-page';
    };
  };
  customTabs?: ServiceTab[]; // Yeni eklenen özel sekmeler
  subMenus?: ServiceSubMenuItem[]; // Menü 3 Hizmetlerimiz flyout
  sectionSettings: SectionSettingsPayload;
}

const defaultServiceSettings: ServiceSettings = {
  heroSection: {
    ...defaultCorporateHeroSectionSettings,
    title: 'Our Professional Services',
    description: 'We provide comprehensive solutions for your business success.',
    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    height: 500,
  },
  storySection: {
    title: 'Our Service Categories',
    description: 'Customized service packages for your different needs',
    backgroundColor: '#ffffff',
    stats: {
      customers: '500+',
      products: '50+',
      support: '7/24',
      satisfaction: '98%'
    }
  },
  valuesSection: {
    title: 'Main Service Areas',
    description: 'Core service categories delivered by our expert team',
    backgroundColor: '#f8fafc',
    values: [
      {
        id: 'business-consulting',
        title: 'Business Consulting',
        description: 'Strategic planning, process optimization, and business development consulting services.',
        icon: 'target',
        color: '#3b82f6'
      },
      {
        id: 'technical-services',
        title: 'Technical Services',
        description: 'System setup, maintenance, updates, and technical support services.',
        icon: 'shield',
        color: '#10b981'
      },
      {
        id: 'training-education',
        title: 'Training and Development',
        description: 'Staff training, certification programs, and skill development.',
        icon: 'users',
        color: '#8b5cf6'
      },
      {
        id: 'project-management',
        title: 'Project Management',
        description: 'Professional management of project planning, execution, and follow-up processes.',
        icon: 'award',
        color: '#f59e0b'
      }
    ]
  },
  whyChooseSection: {
    title: 'Why Choose Us?',
    description: 'We make a difference with our industry experience and customer-focused approach',
    backgroundColor: '#f0f9ff',
    features: [
      {
        id: 'experience',
        title: '10+ Years of Experience',
        description: 'Reliable service backed by many years of industry experience.',
        image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=400&q=80'
      },
      {
        id: 'expert-team',
        title: 'Expert Team',
        description: 'Our team consists of certified professionals who are experts in their fields.',
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=400&q=80'
      },
      {
        id: 'custom-solutions',
        title: 'Custom Solutions',
        description: 'Solution packages designed specifically for each customer\'s needs.',
        image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=400&q=80'
      },
      {
        id: 'support-247',
        title: '24/7 Support',
        description: 'Uninterrupted technical support and customer service.',
        image: 'https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&w=400&q=80'
      },
      {
        id: 'quality-guarantee',
        title: 'Quality Guarantee',
        description: 'Quality guarantee and satisfaction commitment in all our services.',
        image: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=400&q=80'
      },
      {
        id: 'competitive-pricing',
        title: 'Competitive Pricing',
        description: 'High-quality service with highly competitive prices.',
        image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=400&q=80'
      }
    ]
  },
  ctaSection: {
    enabled: true,
    title: 'Ready to Start Your Project?',
    description: 'Turn your dream project into reality with our expert team. We are here to provide solutions tailored for you.',
    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    textColor: '#ffffff',
    primaryButton: {
      enabled: true,
      text: 'Explore Our Services',
      action: 'scroll-to-top'
    },
    secondaryButton: {
      enabled: true,
      text: 'Get Free Consultation',
      action: 'contact-page'
    }
  },
  customTabs: [], // Varsayılan olarak boş
  subMenus: [],
  sectionSettings: {},
};

// CTA renkleri için yardımcılar: gradient/rgb/hex değerinden temsili bir hex çıkar
const extractHexFromColor = (value?: string): string => {
  if (!value) return '#667eea';
  const hexMatch = value.match(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})/);
  if (hexMatch) {
    let h = hexMatch[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    return `#${h.toLowerCase()}`;
  }
  const rgbMatch = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const toHex = (n: string) => Number(n).toString(16).padStart(2, '0');
    return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`;
  }
  return '#667eea';
};

// Arka plana göre okunaklı (zıt) metin rengi önerisi üretir: koyu zeminde beyaz, açık zeminde koyu
const getContrastTextColor = (background?: string): string => {
  const hex = extractHexFromColor(background).replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#111827' : '#ffffff';
};

// CTA (Ek Bölüm) için hazır arka plan önerileri: şablonumuza uygun gradient ve düz renkler
const CTA_BACKGROUND_PRESETS: Array<{ label: string; value: string }> = [
  { label: 'Teal (Şablon)', value: 'linear-gradient(135deg, #0b3c49 0%, #1f8a8f 100%)' },
  { label: 'Mavi-Mor', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { label: 'Gece Mavisi', value: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)' },
  { label: 'Orman', value: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)' },
  { label: 'Gün Batımı', value: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' },
  { label: 'Turuncu-Kırmızı', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { label: 'Okyanus', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { label: 'Antrasit', value: '#111827' },
  { label: 'Lacivert', value: '#1e293b' },
  { label: 'Gök', value: '#0ea5e9' },
  { label: 'Mor', value: '#7c3aed' },
  { label: 'Beyaz', value: '#ffffff' },
];

interface ServiceSettingsComponentProps {
  companyId: string | null;
  configId: string; // SQL ConfigId
  onSettingsChange?: (settings: ServiceSettings) => void;
}

const ServiceSettingsComponent: React.FC<ServiceSettingsComponentProps> = ({ 
  companyId, 
  configId,
  onSettingsChange 
}) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<ServiceSettings>(defaultServiceSettings);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tabToDelete, setTabToDelete] = useState<string | null>(null);
  const [deleteTabName, setDeleteTabName] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [reorderingTabs, setReorderingTabs] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);
  const [selectedValueIndex, setSelectedValueIndex] = useState<number>(-1);
  const [pagePickerOpen, setPagePickerOpen] = useState(false);
  const [pagePickerTargetId, setPagePickerTargetId] = useState<string | null>(null);
  const [featureImageUploading, setFeatureImageUploading] = useState<number | null>(null);
  const theme = useTheme();

  const sortedSubMenus = useMemo(() => {
    return [...(settings.subMenus || [])].sort(
      (a, b) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)
    );
  }, [settings.subMenus]);

  // Ayarları yükle (SQL'den) - Hem sabit ayarlar hem de dinamik sekmeler
  useEffect(() => {
    const loadSettings = async () => {
      if (!companyId || !configId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('📖 [SERVICE SETTINGS] SQL den ayarlar çekiliyor:', { companyId, configId });
        
        // 1. Sabit ayarları çek
        const response = await getServiceSettings(companyId, configId); // ✅ companyId eklendi
        if (response.success && response.data) {
          // Backend'den gelen veriyi frontend formatına çevir
          const backendData = response.data;
          
          // Values JSON'ı parse et
          let valuesArray = [];
          try {
            const rawValues = backendData.valuesJson
              ? JSON.parse(backendData.valuesJson)
              : defaultServiceSettings.valuesSection.values;
            valuesArray = Array.isArray(rawValues)
              ? rawValues.map((item: any) => ({
                  id: String(item?.id ?? uuidv4()),
                  title: String(item?.title ?? ''),
                  description: String(item?.description ?? ''),
                  icon: String(item?.icon ?? 'business'),
                  color: String(item?.color ?? '#3b82f6'),
                  active: item?.active !== false,
                  detailLinkEnabled: item?.detailLinkEnabled === true,
                  detailLinkUrl: String(item?.detailLinkUrl ?? ''),
                  detailLinkText: String(item?.detailLinkText ?? ''),
                }))
              : defaultServiceSettings.valuesSection.values;
          } catch (e) {
            console.warn('⚠️ ValuesJson parse hatası, default kullanılıyor');
            valuesArray = defaultServiceSettings.valuesSection.values;
          }
          
          // Features JSON'ı parse et
          let featuresArray = [];
          try {
            featuresArray = backendData.featuresJson ? JSON.parse(backendData.featuresJson) : defaultServiceSettings.whyChooseSection.features;
          } catch (e) {
            console.warn('⚠️ FeaturesJson parse hatası, default kullanılıyor');
            featuresArray = defaultServiceSettings.whyChooseSection.features;
          }

          let subMenusArray: ServiceSubMenuItem[] = [];
          try {
            const raw = backendData.serviceSubMenusJson
              ? JSON.parse(backendData.serviceSubMenusJson)
              : [];
            if (Array.isArray(raw)) {
              subMenusArray = raw.map((item: any, index: number) => ({
                id: String(item?.id ?? uuidv4()),
                label: String(item?.label ?? ''),
                slug: String(item?.slug ?? ''),
                url: String(item?.url ?? '/'),
                displayOrder: Number(item?.displayOrder ?? index),
                isActive: item?.isActive !== false,
              }));
            }
          } catch (e) {
            console.warn('⚠️ ServiceSubMenusJson parse hatası, boş liste kullanılıyor');
            subMenusArray = [];
          }
          
          const loadedSettings: ServiceSettings = {
            heroSection: normalizeCorporateHeroSection(backendData, {
              title: backendData.heroTitle || defaultServiceSettings.heroSection.title,
              description: backendData.heroDescription || defaultServiceSettings.heroSection.description,
              backgroundColor: backendData.heroBackgroundColor || defaultServiceSettings.heroSection.backgroundColor,
              height: backendData.heroHeight || defaultServiceSettings.heroSection.height,
            }),
            storySection: {
              title: backendData.storyTitle || defaultServiceSettings.storySection.title,
              description: backendData.storyDescription || defaultServiceSettings.storySection.description,
              backgroundColor: backendData.storyBackgroundColor || defaultServiceSettings.storySection.backgroundColor,
              stats: {
                customers: backendData.storyStatCustomers || defaultServiceSettings.storySection.stats.customers,
                products: backendData.storyStatProducts || defaultServiceSettings.storySection.stats.products,
                support: backendData.storyStatSupport || defaultServiceSettings.storySection.stats.support,
                satisfaction: backendData.storyStatSatisfaction || defaultServiceSettings.storySection.stats.satisfaction
              }
            },
            valuesSection: {
              title: backendData.valuesTitle || defaultServiceSettings.valuesSection.title,
              description: backendData.valuesDescription || defaultServiceSettings.valuesSection.description,
              backgroundColor: backendData.valuesBackgroundColor || defaultServiceSettings.valuesSection.backgroundColor,
              values: valuesArray
            },
            whyChooseSection: {
              title: backendData.whyChooseTitle || defaultServiceSettings.whyChooseSection.title,
              description: backendData.whyChooseDescription || defaultServiceSettings.whyChooseSection.description,
              backgroundColor: backendData.whyChooseBackgroundColor || defaultServiceSettings.whyChooseSection.backgroundColor,
              features: featuresArray
            },
            ctaSection: (() => {
              const mergedCta = mergeServiceCtaSettings(
                backendData,
                resolveSectionSettingsFromSource(backendData).ctaSection
              );
              return {
                enabled: mergedCta.enabled,
                title: mergedCta.title,
                description: mergedCta.description,
                backgroundColor: mergedCta.backgroundColor,
                textColor: backendData.ctaTextColor || getContrastTextColor(mergedCta.backgroundColor),
                primaryButton: {
                  enabled: mergedCta.primaryButtonEnabled,
                  text: mergedCta.primaryButtonText,
                  action: (mergedCta.primaryButtonAction === 'scroll-to-top' ? 'scroll-to-top' : 'contact-page') as 'scroll-to-top' | 'contact-page',
                },
                secondaryButton: {
                  enabled: mergedCta.secondaryButtonEnabled,
                  text: mergedCta.secondaryButtonText,
                  action: (mergedCta.secondaryButtonAction === 'scroll-to-top' ? 'scroll-to-top' : 'contact-page') as 'scroll-to-top' | 'contact-page',
                },
              };
            })(),
            customTabs: [], // Dinamik sekmeler ayrı API'den gelecek
            subMenus: subMenusArray,
            sectionSettings: {
              ...resolveSectionSettingsFromSource(backendData),
              ctaSection: mergeServiceCtaSettings(
                backendData,
                resolveSectionSettingsFromSource(backendData).ctaSection
              ),
            },
          };
          
          setSettings(loadedSettings);
          console.log('✅ [SERVICE SETTINGS] SQL den sabit ayarlar yüklendi:', loadedSettings);
        } else {
          // Veri yoksa default kullan
          setSettings(defaultServiceSettings);
          console.log('ℹ️ [SERVICE SETTINGS] Veri bulunamadı, default ayarlar kullanılıyor');
        }
        
        // 2. Dinamik sekmeleri çek (PageType = 'ServiceTab')
        try {
          console.log('📖 [SERVICE TABS] SQL den dinamik sekmeler çekiliyor:', configId);
          const tabsResponse = await getAboutTabs(configId, 'ServiceTab');
          
          if (tabsResponse.success && tabsResponse.data) {
            const dynamicTabs: ServiceTab[] = tabsResponse.data.map((tab: any) => ({
              id: tab.id,
              name: tab.name,
              title: tab.title,
              content: tab.content || '',
              backgroundColor: tab.backgroundColor || '#ffffff',
              order: tab.displayOrder || 0,
              active: tab.isActive,
              sections: parseTabSections<ServiceTabSection>(tab.sections),
              navDisplayMode: tab.navDisplayMode || 'panel'
            }));
            
            setSettings(prev => ({
              ...prev,
              customTabs: dynamicTabs
            }));
            
            console.log('✅ [SERVICE TABS] SQL den dinamik sekmeler yüklendi:', dynamicTabs);
          }
        } catch (tabErr) {
          console.error('❌ [SERVICE TABS] Dinamik sekmeler yüklenirken hata:', tabErr);
          // Hata olsa bile devam et, sadece customTabs boş kalır
        }
        
      } catch (err) {
        console.error('❌ [SERVICE SETTINGS] Ayarlar yüklenirken hata:', err);
        setSettings(defaultServiceSettings);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [companyId, configId]);

  // Auto-save fonksiyonu (her değişiklikte çağrılacak)
  const autoSaveSettings = async (newSettings: ServiceSettings) => {
    if (!companyId || !configId) return;
    
    try {
      console.log('💾 [SERVICE SETTINGS] Auto-saving to SQL...', newSettings);
      
      // Frontend formatını backend formatına çevir
      const backendData = {
        configId,
        companyId,
        ...buildCorporateHeroSavePayload(newSettings.heroSection),
        // Story Section
        storyTitle: newSettings.storySection.title,
        storyDescription: newSettings.storySection.description,
        storyBackgroundColor: newSettings.storySection.backgroundColor,
        storyStatCustomers: newSettings.storySection.stats.customers,
        storyStatProducts: newSettings.storySection.stats.products,
        storyStatSupport: newSettings.storySection.stats.support,
        storyStatSatisfaction: newSettings.storySection.stats.satisfaction,
        // Values Section
        valuesTitle: newSettings.valuesSection.title,
        valuesDescription: newSettings.valuesSection.description,
        valuesBackgroundColor: newSettings.valuesSection.backgroundColor,
        valuesJson: JSON.stringify(newSettings.valuesSection.values),
        // Why Choose Section
        whyChooseTitle: newSettings.whyChooseSection.title,
        whyChooseDescription: newSettings.whyChooseSection.description,
        whyChooseBackgroundColor: newSettings.whyChooseSection.backgroundColor,
        featuresJson: JSON.stringify(newSettings.whyChooseSection.features),
        sectionSettingsJson: serializeSectionSettingsJson(newSettings.sectionSettings || {}),
        // CTA Section
        ctaEnabled: newSettings.ctaSection.enabled,
        ctaTitle: newSettings.ctaSection.title,
        ctaDescription: newSettings.ctaSection.description,
        ctaBackgroundColor: newSettings.ctaSection.backgroundColor,
        ctaTextColor: newSettings.ctaSection.textColor,
        ctaPrimaryButtonEnabled: newSettings.ctaSection.primaryButton.enabled,
        ctaPrimaryButtonText: newSettings.ctaSection.primaryButton.text,
        ctaPrimaryButtonAction:
          newSettings.sectionSettings?.ctaSection?.primaryButtonAction ||
          newSettings.ctaSection.primaryButton.action,
        ctaSecondaryButtonEnabled: newSettings.ctaSection.secondaryButton.enabled,
        ctaSecondaryButtonText: newSettings.ctaSection.secondaryButton.text,
        ctaSecondaryButtonAction:
          newSettings.sectionSettings?.ctaSection?.secondaryButtonAction ||
          newSettings.ctaSection.secondaryButton.action,
        serviceSubMenusJson: JSON.stringify(newSettings.subMenus || []),
      };
      
      await saveServiceSettings(backendData);
      console.log('✅ [SERVICE SETTINGS] Auto-save başarılı');
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
    } catch (err) {
      console.error('❌ [SERVICE SETTINGS] Auto-save hatası:', err);
    }
  };

  // Varsayılan ayarlara sıfırla
  const handleReset = () => {
    setSettings(defaultServiceSettings);
    autoSaveSettings(defaultServiceSettings);
    if (onSettingsChange) {
      onSettingsChange(defaultServiceSettings);
    }
  };

  const applySettings = (newSettings: ServiceSettings) => {
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const updateSubMenus = (updater: (items: ServiceSubMenuItem[]) => ServiceSubMenuItem[]) => {
    setSettings((prev) => {
      const newSettings: ServiceSettings = {
        ...prev,
        subMenus: updater(prev.subMenus || []),
      };
      autoSaveSettings(newSettings);
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }
      return newSettings;
    });
  };

  const addSubMenu = () => {
    updateSubMenus((items) => [
      ...items,
      {
        id: uuidv4(),
        label: '',
        slug: '',
        url: '',
        displayOrder: items.length,
        isActive: true,
      },
    ]);
  };

  const openPagePickerFor = (itemId: string) => {
    setPagePickerTargetId(itemId);
    setPagePickerOpen(true);
  };

  const handlePagePickerSelect = (selection: { url: string; title: string; slug: string }) => {
    if (!pagePickerTargetId) return;
    updateSubMenus((items) =>
      items.map((item) =>
        item.id === pagePickerTargetId
          ? {
              ...item,
              url: selection.url,
              slug: selection.slug,
              label: item.label?.trim() ? item.label : selection.title,
            }
          : item
      )
    );
    setPagePickerOpen(false);
    setPagePickerTargetId(null);
  };

  const updateHeroSection = (heroSection: CorporateHeroSectionSettings) => {
    setSettings((prev) => {
      // Sayfa bütünlüğü için: Hero arka planı (template/gradient) değişince CTA da otomatik eşitlenir
      const heroBgChanged = prev.heroSection?.backgroundColor !== heroSection.backgroundColor;
      const nextCtaSection = heroBgChanged && heroSection.backgroundColor
        ? {
            ...prev.ctaSection,
            backgroundColor: heroSection.backgroundColor,
            textColor: getContrastTextColor(heroSection.backgroundColor),
          }
        : prev.ctaSection;

      const newSettings = {
        ...prev,
        heroSection,
        ctaSection: nextCtaSection,
      };
      autoSaveSettings(newSettings);
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }
      return newSettings;
    });
  };

  const updateSectionSettings = (
    sectionKey: keyof SectionSettingsPayload,
    patch: Record<string, unknown>
  ) => {
    const newSettings = {
      ...settings,
      sectionSettings: {
        ...settings.sectionSettings,
        [sectionKey]: {
          ...(settings.sectionSettings?.[sectionKey] || {}),
          ...patch,
        },
      },
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Story section güncelle
  const updateStorySection = (field: keyof typeof settings.storySection, value: any) => {
    const newSettings = {
      ...settings,
      storySection: {
        ...settings.storySection,
        [field]: value
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Story stats güncelle
  const updateStoryStat = (statKey: keyof typeof settings.storySection.stats, value: string) => {
    const newSettings = {
      ...settings,
      storySection: {
        ...settings.storySection,
        stats: {
          ...settings.storySection.stats,
          [statKey]: value
        }
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Values section güncelle
  const updateValuesSection = (field: keyof typeof settings.valuesSection, value: any) => {
    const newSettings = {
      ...settings,
      valuesSection: {
        ...settings.valuesSection,
        [field]: value
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Değer güncelle
  const updateValue = (index: number, field: keyof typeof settings.valuesSection.values[0], value: string | boolean) => {
    const newSettings = {
      ...settings,
      valuesSection: {
        ...settings.valuesSection,
        values: settings.valuesSection.values.map((val, i) => 
          i === index ? { ...val, [field]: value } : val
        )
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Yeni değer ekle
  const addNewValue = () => {
    const newValue = {
      id: uuidv4(),
      title: 'Yeni Hizmet',
      description: 'Service description will be written here.',
      icon: 'business',
      color: '#3b82f6',
      active: true,
      detailLinkEnabled: false,
      detailLinkUrl: '',
      detailLinkText: '',
    };

    const newSettings = {
      ...settings,
      valuesSection: {
        ...settings.valuesSection,
        values: [...settings.valuesSection.values, newValue]
      }
    };

    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Değer sil
  const deleteValue = (index: number) => {
    const newSettings = {
      ...settings,
      valuesSection: {
        ...settings.valuesSection,
        values: settings.valuesSection.values.filter((_, i) => i !== index)
      }
    };

    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Değer aktif/pasif
  const toggleValueActive = (index: number) => {
    const currentValue = settings.valuesSection.values[index];
    updateValue(index, 'active', !currentValue.active);
  };

  // İkon seçici aç
  const openIconSelector = (valueIndex: number) => {
    setSelectedValueIndex(valueIndex);
    setIconSelectorOpen(true);
  };

  // İkon seç
  const handleIconSelect = (iconName: string) => {
    if (selectedValueIndex >= 0) {
      updateValue(selectedValueIndex, 'icon', iconName);
    }
    setIconSelectorOpen(false);
    setSelectedValueIndex(-1);
  };

  // Why choose section güncelle
  const updateWhyChooseSection = (field: keyof typeof settings.whyChooseSection, value: any) => {
    const newSettings = {
      ...settings,
      whyChooseSection: {
        ...settings.whyChooseSection,
        [field]: value
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const serviceCtaSettings = resolveAboutCtaSection(settings.sectionSettings?.ctaSection, {
    defaultDisplayPages: ['services'],
  });

  const updateCorporateCta = (patch: Partial<AboutCtaSectionExtras>) => {
    const next = { ...serviceCtaSettings, ...patch };
    applySettings({
      ...settings,
      ctaSection: {
        enabled: next.enabled,
        title: next.title,
        description: next.description,
        backgroundColor: next.backgroundColor,
        textColor: settings.ctaSection.textColor,
        primaryButton: {
          enabled: next.primaryButtonEnabled,
          text: next.primaryButtonText,
          action: next.primaryButtonAction === 'scroll-to-top' ? 'scroll-to-top' : 'contact-page',
        },
        secondaryButton: {
          enabled: next.secondaryButtonEnabled,
          text: next.secondaryButtonText,
          action: next.secondaryButtonAction === 'scroll-to-top' ? 'scroll-to-top' : 'contact-page',
        },
      },
      sectionSettings: {
        ...settings.sectionSettings,
        ctaSection: next,
      },
    });
  };

  // CTA section güncelle
  const updateCtaSection = (field: keyof typeof settings.ctaSection, value: any) => {
    const newSettings = {
      ...settings,
      ctaSection: {
        ...settings.ctaSection,
        [field]: value
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // CTA arka planını (preset veya seçim) uygular ve yazı rengini otomatik zıt renge ayarlar
  const applyCtaBackground = (background: string) => {
    const newSettings = {
      ...settings,
      ctaSection: {
        ...settings.ctaSection,
        backgroundColor: background,
        textColor: getContrastTextColor(background),
      },
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // CTA button güncelle
  const updateCtaButton = (buttonType: 'primaryButton' | 'secondaryButton', field: keyof typeof settings.ctaSection.primaryButton, value: any) => {
    const newSettings = {
      ...settings,
      ctaSection: {
        ...settings.ctaSection,
        [buttonType]: {
          ...settings.ctaSection[buttonType],
          [field]: value
        }
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Özellik güncelle
  const updateFeature = (index: number, field: keyof typeof settings.whyChooseSection.features[0], value: string) => {
    const newSettings = {
      ...settings,
      whyChooseSection: {
        ...settings.whyChooseSection,
        features: settings.whyChooseSection.features.map((feat, i) => 
          i === index ? { ...feat, [field]: value } : feat
        )
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Özellikler ikişerli yönetilir: 0, 2, 4 veya 6 olabilir (maksimum 6)
  const MAX_FEATURES = 6;

  // İkili özellik ekle (2 boş özellik ekler)
  const addFeaturePair = () => {
    if (settings.whyChooseSection.features.length >= MAX_FEATURES) return;
    const baseId = Date.now();
    const newFeatures = [
      ...settings.whyChooseSection.features,
      { id: `feature-${baseId}-a`, title: '', description: '' },
      { id: `feature-${baseId}-b`, title: '', description: '' }
    ];
    const newSettings = {
      ...settings,
      whyChooseSection: {
        ...settings.whyChooseSection,
        features: newFeatures
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // İkili özellik çıkar (son 2 özelliği siler)
  const removeFeaturePair = () => {
    if (settings.whyChooseSection.features.length < 2) return;
    const newFeatures = settings.whyChooseSection.features.slice(0, -2);
    const newSettings = {
      ...settings,
      whyChooseSection: {
        ...settings.whyChooseSection,
        features: newFeatures
      }
    };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Özellik görseli yükle (Image Service - CorporateHero ile aynı yol)
  const uploadFeatureImage = async (index: number, file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Lütfen geçerli bir görsel dosyası seçin.');
      return;
    }
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      alert('Görsel boyutu çok büyük (maksimum 10MB). Lütfen daha küçük bir dosya seçin.');
      return;
    }
    try {
      setFeatureImageUploading(index);
      const [uploaded] = await ImageUploadService.uploadImages([file]);
      if (!uploaded?.originalUrl) {
        throw new Error('Görsel servisi boş URL döndürdü');
      }
      updateFeature(index, 'image', uploaded.originalUrl);
    } catch (e: any) {
      console.error('⚠️ Özellik görseli yüklenemedi:', e);
      alert('Görsel yüklenirken bir hata oluştu: ' + (e?.message || 'Bilinmeyen hata') + '\nLütfen tekrar deneyin.');
    } finally {
      setFeatureImageUploading(null);
    }
  };

  // Özellik görselini kaldır (numara gösterimine döner)
  const removeFeatureImage = (index: number) => {
    updateFeature(index, 'image', '');
  };

  // Tab değişikliği
  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  // Ana sekme sırasını kaydet (anasayfa modülleri gibi sürükle-bırak)
  const handleTabsReorder = async (reorderedItems: { id: string; name: string }[]) => {
    if (!companyId || !configId) return;

    const tabMap = new Map((settings.customTabs || []).map((tab) => [tab.id, tab]));
    const normalizedTabs = reorderedItems
      .map((item, index) => {
        const existing = tabMap.get(item.id);
        if (!existing) return null;
        return { ...existing, order: index };
      })
      .filter((tab): tab is ServiceTab => tab !== null);

    const previousSettings = settings;
    const sortedPrevious = [...(settings.customTabs || [])].sort((a, b) => a.order - b.order);
    const activeTabId = activeTab > 0 ? sortedPrevious[activeTab - 1]?.id : null;
    const nextActiveIndex = activeTabId
      ? normalizedTabs.findIndex((tab) => tab.id === activeTabId) + 1
      : 0;

    const newSettings = {
      ...settings,
      customTabs: normalizedTabs,
    };

    setSettings(newSettings);
    if (activeTab > 0 && nextActiveIndex > 0) {
      setActiveTab(nextActiveIndex);
    }
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    try {
      setReorderingTabs(true);
      setReorderError(null);
      await Promise.all(
        normalizedTabs.map((tab) =>
          saveAboutTab({
            id: tab.id,
            configId,
            companyId,
            pageType: 'ServiceTab',
            name: tab.name,
            title: tab.title,
            content: tab.content || '',
            backgroundColor: tab.backgroundColor || '#ffffff',
            displayOrder: tab.order,
            isActive: tab.active,
            sections: JSON.stringify(tab.sections || []),
            navDisplayMode: tab.navDisplayMode || 'panel',
          })
        )
      );
    } catch (error) {
      console.error('❌ [SERVICE TAB] Sıra kaydedilirken hata:', error);
      setSettings(previousSettings);
      if (onSettingsChange) {
        onSettingsChange(previousSettings);
      }
      setReorderError(t('ecommerceServiceSettings.tabs.reorderError'));
    } finally {
      setReorderingTabs(false);
    }
  };

  // Yeni sekme ekle
  const handleAddTab = async (tabName: string) => {
    if (!companyId || !configId) return;

    const newTab: ServiceTab = {
      id: '', // Backend tarafından oluşturulacak
      name: tabName,
      title: tabName,
      content: '',
      backgroundColor: '#ffffff',
      order: (settings.customTabs?.length || 0) + 1,
      active: true,
      sections: [],
      navDisplayMode: 'panel'
    };

    try {
      console.log('💾 [SERVICE TABS] Yeni sekme SQL e kaydediliyor:', newTab);
      
      // SQL'e kaydet
      const response = await saveAboutTab({
        id: '', // Yeni kayıt için boş
        configId,
        companyId,
        pageType: 'ServiceTab',
        name: newTab.name,
        title: newTab.title,
        content: newTab.content,
        backgroundColor: newTab.backgroundColor,
        displayOrder: newTab.order,
        isActive: newTab.active,
        sections: JSON.stringify(newTab.sections || []),
        navDisplayMode: newTab.navDisplayMode
      });

      if (response.success && response.data) {
        // Backend'den dönen ID ile güncelle
        newTab.id = response.data.id;
        
        const newSettings = {
          ...settings,
          customTabs: [...(settings.customTabs || []), newTab]
        };

        setSettings(newSettings);
        
        if (onSettingsChange) {
          onSettingsChange(newSettings);
        }

        // Yeni eklenen sekmeyi aktif yap
        setActiveTab((settings.customTabs?.length || 0) + 1);
        
        console.log('✅ [SERVICE TABS] Yeni sekme başarıyla kaydedildi:', newTab);
      }
    } catch (error) {
      console.error('❌ [SERVICE TABS] Yeni sekme eklenirken hata:', error);
      alert('An error occurred while adding the tab. Please try again.');
    }
  };

  // Sekme silme başlat
  const handleDeleteTab = (tabId: string, tabName: string) => {
    setTabToDelete(tabId);
    setDeleteTabName(tabName);
    setDeleteModalOpen(true);
  };

  // Sekme silme onay
  const confirmDeleteTab = async () => {
    if (!tabToDelete || !companyId) return;

    setDeleting(true);

    try {
      console.log('🗑️ [SERVICE TABS] Sekme SQL den siliniyor:', tabToDelete);
      
      // SQL'den sil
      await deleteAboutTab(tabToDelete, companyId); // ✅ companyId eklendi
      
      // Local state'i güncelle
      const newSettings = {
        ...settings,
        customTabs: (settings.customTabs || []).filter(tab => tab.id !== tabToDelete)
      };

      setSettings(newSettings);
      
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }

      // Güvenli bir sekmeye geçiş yap
      setActiveTab(0);
      
      // Modal'ı kapat
      setDeleteModalOpen(false);
      setTabToDelete(null);
      setDeleteTabName('');
      
      console.log('✅ [SERVICE TABS] Sekme başarıyla silindi');
      
    } catch (error) {
      console.error('❌ [SERVICE TABS] Sekme silinirken hata:', error);
      alert('An error occurred while deleting the tab. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Sekme silme iptal
  const cancelDeleteTab = () => {
    setDeleteModalOpen(false);
    setTabToDelete(null);
    setDeleteTabName('');
  };

  // Sekme güncelle
  const updateTab = async (tabId: string, field: keyof ServiceTab, value: any) => {
    if (!companyId || !configId) return;

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId ? { ...tab, [field]: value } : tab
      )
    };

    setSettings(newSettings);
    
    // SQL'e kaydet
    const updatedTab = newSettings.customTabs?.find(t => t.id === tabId);
    if (updatedTab) {
      try {
        console.log('💾 [SERVICE TABS] Sekme SQL e kaydediliyor:', updatedTab);
        
        await saveAboutTab({
          id: updatedTab.id,
          configId,
          companyId,
          pageType: 'ServiceTab',
          name: updatedTab.name,
          title: updatedTab.title,
          content: updatedTab.content,
          backgroundColor: updatedTab.backgroundColor,
          displayOrder: updatedTab.order,
          isActive: updatedTab.active,
          sections: JSON.stringify(updatedTab.sections || []),
          navDisplayMode: updatedTab.navDisplayMode || 'panel'
        });
        
        console.log('✅ [SERVICE TABS] Sekme başarıyla güncellendi');
      } catch (error) {
        console.error('❌ [SERVICE TABS] Sekme güncellenirken hata:', error);
      }
    }
    
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Sekme aktif/pasif
  const toggleTabActive = (tabId: string) => {
    updateTab(tabId, 'active', !(settings.customTabs?.find(tab => tab.id === tabId)?.active || false));
  };

  // Yeni bölüm ekle
  const addNewSection = async (tabId: string) => {
    const newSection: ServiceTabSection = {
      id: uuidv4(),
      title: '',
      content: '',
      image: '',
      imagePosition: 'left',
      imageSize: 'equal',
      imageAnimation: 'zoom',
    };

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { ...tab, sections: [...(tab.sections || []), newSection] }
          : tab
      )
    };

    setSettings(newSettings);
    
    // SQL'e kaydet
    const updatedTab = newSettings.customTabs?.find(t => t.id === tabId);
    if (updatedTab && companyId && configId) {
      try {
        await saveAboutTab({
          id: updatedTab.id,
          configId,
          companyId,
          pageType: 'ServiceTab',
          name: updatedTab.name,
          title: updatedTab.title,
          content: updatedTab.content,
          backgroundColor: updatedTab.backgroundColor,
          displayOrder: updatedTab.order,
          isActive: updatedTab.active,
          sections: JSON.stringify(updatedTab.sections || []),
          navDisplayMode: updatedTab.navDisplayMode || 'panel'
        });
        console.log('✅ [SERVICE TABS] Yeni bölüm eklendi ve SQL e kaydedildi');
      } catch (error) {
        console.error('❌ [SERVICE TABS] Bölüm eklenirken hata:', error);
      }
    }
    
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Bölüm sil
  const deleteSection = async (tabId: string, sectionId: string) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { ...tab, sections: (tab.sections || []).filter(section => section.id !== sectionId) }
          : tab
      )
    };

    setSettings(newSettings);
    
    // SQL'e kaydet
    const updatedTab = newSettings.customTabs?.find(t => t.id === tabId);
    if (updatedTab && companyId && configId) {
      try {
        await saveAboutTab({
          id: updatedTab.id,
          configId,
          companyId,
          pageType: 'ServiceTab',
          name: updatedTab.name,
          title: updatedTab.title,
          content: updatedTab.content,
          backgroundColor: updatedTab.backgroundColor,
          displayOrder: updatedTab.order,
          isActive: updatedTab.active,
          sections: JSON.stringify(updatedTab.sections || []),
          navDisplayMode: updatedTab.navDisplayMode || 'panel'
        });
        console.log('✅ [SERVICE TABS] Bölüm silindi ve SQL e kaydedildi');
      } catch (error) {
        console.error('❌ [SERVICE TABS] Bölüm silinirken hata:', error);
      }
    }
    
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Bölüm güncelle
  const updateSection = async (tabId: string, sectionId: string, field: keyof ServiceTabSection, value: string) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { 
              ...tab, 
              sections: (tab.sections || []).map(section => 
                section.id === sectionId 
                  ? { ...section, [field]: value }
                  : section
              )
            }
          : tab
      )
    };

    setSettings(newSettings);
    
    // SQL'e kaydet
    const updatedTab = newSettings.customTabs?.find(t => t.id === tabId);
    if (updatedTab && companyId && configId) {
      try {
        await saveAboutTab({
          id: updatedTab.id,
          configId,
          companyId,
          pageType: 'ServiceTab',
          name: updatedTab.name,
          title: updatedTab.title,
          content: updatedTab.content,
          backgroundColor: updatedTab.backgroundColor,
          displayOrder: updatedTab.order,
          isActive: updatedTab.active,
          sections: JSON.stringify(updatedTab.sections || []),
          navDisplayMode: updatedTab.navDisplayMode || 'panel'
        });
        console.log('✅ [SERVICE TABS] Bölüm güncellendi ve SQL e kaydedildi');
      } catch (error) {
        console.error('❌ [SERVICE TABS] Bölüm güncellenirken hata:', error);
      }
    }
    
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const reorderSections = async (tabId: string, nextSections: ServiceTabSection[]) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab =>
        tab.id === tabId
          ? { ...tab, sections: nextSections }
          : tab
      )
    };

    setSettings(newSettings);

    const updatedTab = newSettings.customTabs?.find(t => t.id === tabId);
    if (updatedTab && companyId && configId) {
      try {
        await saveAboutTab({
          id: updatedTab.id,
          configId,
          companyId,
          pageType: 'ServiceTab',
          name: updatedTab.name,
          title: updatedTab.title,
          content: updatedTab.content,
          backgroundColor: updatedTab.backgroundColor,
          displayOrder: updatedTab.order,
          isActive: updatedTab.active,
          sections: JSON.stringify(updatedTab.sections || []),
          navDisplayMode: updatedTab.navDisplayMode || 'panel'
        });
      } catch (error) {
        console.error('❌ [SERVICE TABS] Bölüm sırası kaydedilemedi:', error);
      }
    }

    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const customTabs = (settings.customTabs || []).sort((a, b) => a.order - b.order);

  return (
    <Paper elevation={3} sx={{ position: 'relative', zIndex: 1 }}>
      {/* Tab Header */}
      <Box sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pt: 1,
          px: 1,
          backgroundColor: theme.palette.grey[200],
      }}>
          <SortableSubTabs
            generalLabel={t('ecommerceServiceSettings.tabs.serviceSettings')}
            tabs={customTabs.map((tab) => ({ id: tab.id, name: tab.name }))}
            activeIndex={activeTab}
            reordering={reorderingTabs}
            dragHandleTitle={t('ecommerceServiceSettings.tabs.dragToReorder')}
            ariaLabel={t('ecommerceServiceSettings.tabs.ariaLabel')}
            onSelectTab={handleTabChange}
            onReorder={handleTabsReorder}
          />
          {reorderingTabs && (
            <CircularProgress size={18} sx={{ ml: 1, flexShrink: 0 }} />
          )}
          <Tooltip title={t('ecommerceAboutSettings.tabs.addNewTab')}>
              <IconButton
                size="small"
                sx={{ ml: 1, flexShrink: 0 }}
                onClick={() => setIsModalOpen(true)}
                disabled={reorderingTabs}
              >
                  <AddIcon />
              </IconButton>
          </Tooltip>
      </Box>

      {reorderError && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setReorderError(null)}>
          {reorderError}
        </Alert>
      )}

      {/* Ana Hizmet Ayarları Tab */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            <InfoIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            {t('ecommerceServiceSettings.title')}
            <GuideButton guideKey="ecommerce.services" size="small" sx={{ ml: 0.5 }} />
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('ecommerceAboutSettings.actions.resetToDefaultSettings')}>
              <IconButton onClick={handleReset} color="secondary">
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

      {/* Hero Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceServiceSettings.sections.hero')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CorporateHeroSettingsPanel
            layoutMode="color-wash"
            value={settings.heroSection}
            onChange={updateHeroSection}
            companyId={companyId}
            configId={configId}
            defaultHeight={500}
            sectionOptions={buildHeroSectionOptions(
              [
                {
                  label: settings.storySection?.title || 'Hizmetler',
                  value: 'services-story',
                },
                {
                  label: settings.valuesSection?.title || 'Değerler',
                  value: 'services-values',
                },
                {
                  label: settings.whyChooseSection?.title || 'Neden Biz',
                  value: 'services-why-choose',
                },
                {
                  label: settings.ctaSection?.title || 'Başlayın',
                  value: 'services-cta',
                },
                { label: 'SSS', value: 'services_faq' },
              ],
              settings.customTabs
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* Alt Menü (Menü 3 Hizmetlerimiz flyout) */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <TabIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceServiceSettings.sections.subMenus')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {sortedSubMenus.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ecommerceServiceSettings.subMenus.empty')}
            </Typography>
          ) : (
            sortedSubMenus.map((item, index, arr) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: 'grey.50' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm="auto">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={item.isActive !== false}
                          onChange={(e) =>
                            updateSubMenus((items) =>
                              items.map((it) =>
                                it.id === item.id ? { ...it, isActive: e.target.checked } : it
                              )
                            )
                          }
                        />
                      }
                      label={t('ecommerceServiceSettings.subMenus.active')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('ecommerceServiceSettings.subMenus.label')}
                      value={item.label}
                      onChange={(e) =>
                        updateSubMenus((items) =>
                          items.map((it) =>
                            it.id === item.id ? { ...it, label: e.target.value } : it
                          )
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('ecommerceServiceSettings.subMenus.url')}
                      value={item.url}
                      onChange={(e) =>
                        updateSubMenus((items) =>
                          items.map((it) =>
                            it.id === item.id ? { ...it, url: e.target.value } : it
                          )
                        )
                      }
                      placeholder="/about"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={t('ecommerceServiceSettings.subMenus.pickPage')}>
                              <span>
                                <IconButton
                                  size="small"
                                  edge="end"
                                  onClick={() => openPagePickerFor(item.id)}
                                  disabled={!configId}
                                >
                                  <SearchIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                      <Tooltip title={t('ecommerceServiceSettings.subMenus.moveUp')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => {
                              if (index <= 0) return;
                              updateSubMenus((items) => {
                                const next = [...items].sort(
                                  (a, b) => a.displayOrder - b.displayOrder
                                );
                                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                return next.map((it, i) => ({ ...it, displayOrder: i }));
                              });
                            }}
                          >
                            <KeyboardArrowUpIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('ecommerceServiceSettings.subMenus.moveDown')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index >= arr.length - 1}
                            onClick={() => {
                              if (index >= arr.length - 1) return;
                              updateSubMenus((items) => {
                                const next = [...items].sort(
                                  (a, b) => a.displayOrder - b.displayOrder
                                );
                                [next[index + 1], next[index]] = [next[index], next[index + 1]];
                                return next.map((it, i) => ({ ...it, displayOrder: i }));
                              });
                            }}
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('ecommerceServiceSettings.subMenus.remove')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            updateSubMenus((items) => items.filter((it) => it.id !== item.id))
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                  {item.slug ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        {t('ecommerceServiceSettings.subMenus.slug')}: {item.slug}
                      </Typography>
                    </Grid>
                  ) : null}
                </Grid>
              </Paper>
            ))
          )}
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addSubMenu}>
            {t('ecommerceServiceSettings.subMenus.add')}
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Story Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceServiceSettings.sections.category')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionVisibilityToggle
                enabled={settings.sectionSettings?.storySection?.enabled !== false}
                onChange={(enabled) => updateSectionSettings('storySection', { enabled })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.title')}
                value={settings.storySection.title}
                onChange={(e) => updateStorySection('title', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('ecommerceAboutSettings.fields.description')}
                value={settings.storySection.description}
                onChange={(e) => updateStorySection('description', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={settings.storySection.backgroundColor}
                onChange={(e) => updateStorySection('backgroundColor', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={settings.storySection.backgroundColor}
                        onChange={(e) => updateStorySection('backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('ecommerceAboutSettings.sections.statistics')}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.stats.happyCustomers')}
                value={settings.storySection.stats.customers}
                onChange={(e) => updateStoryStat('customers', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('ecommerceServiceSettings.stats.productRange')}
                value={settings.storySection.stats.products}
                onChange={(e) => updateStoryStat('products', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.stats.customerSupport')}
                value={settings.storySection.stats.support}
                onChange={(e) => updateStoryStat('support', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.stats.satisfactionRate')}
                value={settings.storySection.stats.satisfaction}
                onChange={(e) => updateStoryStat('satisfaction', e.target.value)}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Values Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceServiceSettings.sections.ourServices')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionVisibilityToggle
                enabled={settings.sectionSettings?.valuesSection?.enabled !== false}
                onChange={(enabled) => updateSectionSettings('valuesSection', { enabled })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.title')}
                value={settings.valuesSection.title}
                onChange={(e) => updateValuesSection('title', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('ecommerceAboutSettings.fields.description')}
                value={settings.valuesSection.description}
                onChange={(e) => updateValuesSection('description', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={settings.valuesSection.backgroundColor}
                onChange={(e) => updateValuesSection('backgroundColor', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={settings.valuesSection.backgroundColor}
                        onChange={(e) => updateValuesSection('backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('ecommerceServiceSettings.sections.serviceAreasWithCount', { count: settings.valuesSection.values.length })}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addNewValue}
                  size="small"
                >
                  {t('ecommerceServiceSettings.actions.addNewService')}
                </Button>
              </Box>
            </Grid>
            
            {settings.valuesSection.values.map((value, index) => (
              <Grid item xs={12} key={value.id}>
                <Card variant="outlined" sx={{ p: 2, position: 'relative' }}>
                  {/* Durum ve Silme Butonları */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {value.title || t('ecommerceServiceSettings.sections.service')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={value.active !== false}
                            onChange={() => toggleValueActive(index)}
                            size="small"
                          />
                        }
                        label={value.active !== false ? t('ecommerceAboutSettings.customTabs.active') : t('ecommerceAboutSettings.customTabs.hidden')}
                        sx={{ mr: 1 }}
                      />
                      <Tooltip title={t('ecommerceServiceSettings.actions.deleteThisService')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteValue(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t('ecommerceAboutSettings.fields.title')}
                        value={value.title}
                        onChange={(e) => updateValue(index, 'title', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label={t('ecommerceAboutSettings.fields.description')}
                        value={value.description}
                        onChange={(e) => updateValue(index, 'description', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label={t('ecommerceAboutSettings.fields.color')}
                        value={value.color}
                        onChange={(e) => updateValue(index, 'color', e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                component="input"
                                type="color"
                                value={value.color}
                                onChange={(e) => updateValue(index, 'color', e.target.value)}
                                sx={{ border: 'none', width: '20px', height: '20px', p: 0, background: 'none', cursor: 'pointer' }}
                              />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="caption" gutterBottom>
                          {t('ecommerceServiceSettings.fields.icon')}
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => openIconSelector(index)}
                          sx={{ 
                            minWidth: '60px',
                            height: '60px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5
                          }}
                        >
                          {(() => {
                            const iconData = ICON_LIBRARY.find(icon => icon.name === value.icon);
                            if (iconData) {
                              const IconComponent = iconData.icon;
                              return <IconComponent sx={{ fontSize: 24 }} />;
                            }
                            return <PaletteIcon sx={{ fontSize: 24 }} />;
                          })()}
                          <Typography variant="caption" sx={{ fontSize: '10px' }}>
                            {value.icon}
                          </Typography>
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={value.detailLinkEnabled === true}
                            onChange={(e) =>
                              updateValue(index, 'detailLinkEnabled', e.target.checked)
                            }
                          />
                        }
                        label={t('ecommerceServiceSettings.detailLink.enabled')}
                      />
                    </Grid>
                    {value.detailLinkEnabled === true && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('ecommerceServiceSettings.detailLink.url')}
                            value={value.detailLinkUrl || ''}
                            onChange={(e) => updateValue(index, 'detailLinkUrl', e.target.value)}
                            placeholder="/services"
                            helperText={t('ecommerceServiceSettings.detailLink.urlHelp')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('ecommerceServiceSettings.detailLink.text')}
                            value={value.detailLinkText || ''}
                            onChange={(e) => updateValue(index, 'detailLinkText', e.target.value)}
                            placeholder={t('ecommerceServiceSettings.detailLink.textPlaceholder')}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {/* Durum Göstergesi */}
                  <Box sx={{ 
                    mt: 2, 
                    p: 1, 
                    backgroundColor: value.active !== false ? '#e8f5e8' : '#fff3cd', 
                    borderRadius: 1,
                    border: `1px solid ${value.active !== false ? '#4caf50' : '#ff9800'}`
                  }}>
                    <Typography variant="caption" color={value.active !== false ? 'success.main' : 'warning.main'}>
                      {value.active !== false ? t('ecommerceServiceSettings.status.serviceActiveInfo') : t('ecommerceServiceSettings.status.serviceHiddenInfo')}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Why Choose Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceAboutSettings.sections.whyChooseUs')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionVisibilityToggle
                enabled={settings.sectionSettings?.whyChooseSection?.enabled !== false}
                onChange={(enabled) => updateSectionSettings('whyChooseSection', { enabled })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.title')}
                value={settings.whyChooseSection.title}
                onChange={(e) => updateWhyChooseSection('title', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('ecommerceAboutSettings.fields.description')}
                value={settings.whyChooseSection.description}
                onChange={(e) => updateWhyChooseSection('description', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={settings.whyChooseSection.backgroundColor}
                onChange={(e) => updateWhyChooseSection('backgroundColor', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={settings.whyChooseSection.backgroundColor}
                        onChange={(e) => updateWhyChooseSection('backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('ecommerceAboutSettings.sections.features')} ({settings.whyChooseSection.features.length}/{MAX_FEATURES})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RemoveIcon />}
                    onClick={removeFeaturePair}
                    disabled={settings.whyChooseSection.features.length < 2}
                  >
                    İkili Çıkar
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addFeaturePair}
                    disabled={settings.whyChooseSection.features.length >= MAX_FEATURES}
                  >
                    İkili Ekle
                  </Button>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Özellikler ikişerli eklenir/çıkarılır (2, 4 veya 6). Hiç özellik olmadığında bu bölüm sayfada gizlenir.
              </Typography>
            </Grid>
            
            {settings.whyChooseSection.features.map((feature, index) => (
              <Grid item xs={12} md={6} key={feature.id}>
                <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('ecommerceAboutSettings.fields.title')}
                        value={feature.title}
                        onChange={(e) => updateFeature(index, 'title', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label={t('ecommerceAboutSettings.fields.description')}
                        value={feature.description}
                        onChange={(e) => updateFeature(index, 'description', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    {/* Opsiyonel görsel: doluysa mağazada numara yerine görsel gösterilir */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2,
                            flexShrink: 0,
                            overflow: 'hidden',
                            border: '1px dashed',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: feature.image ? 'transparent' : 'grey.50'
                          }}
                        >
                          {feature.image ? (
                            <Box component="img" src={feature.image} alt={feature.title}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <ImageIcon sx={{ color: 'text.disabled' }} />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Button
                            component="label"
                            variant="outlined"
                            size="small"
                            startIcon={<ImageIcon />}
                            disabled={featureImageUploading === index}
                          >
                            {featureImageUploading === index
                              ? 'Yükleniyor...'
                              : feature.image ? 'Görseli Değiştir' : 'Görsel Ekle'}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) uploadFeatureImage(index, file);
                                e.target.value = '';
                              }}
                            />
                          </Button>
                          {feature.image && (
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => removeFeatureImage(index)}
                            >
                              Görseli Kaldır
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* CTA Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <PaletteIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceServiceSettings.sections.extraCta')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CorporateCtaSettingsPanel
            value={serviceCtaSettings}
            onChange={updateCorporateCta}
          />
        </AccordionDetails>
      </Accordion>
      </TabPanel>

      {/* Özel Sekme Tab'ları */}
      {customTabs.map((tab, index) => (
        <TabPanel key={tab.id} value={activeTab} index={index + 1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              <TabIcon sx={{ mr: 1.5, color: 'primary.main' }} />
              {t('ecommerceAboutSettings.customTabs.tabSettingsTitle', { tabName: tab.name })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('ecommerceAboutSettings.customTabs.deleteThisTab')}>
                <IconButton 
                  onClick={() => handleDeleteTab(tab.id, tab.name)} 
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceServiceSettings.customTabs.tabNameVisibleSidebar')}
                value={tab.name}
                onChange={(e) => updateTab(tab.id, 'name', e.target.value)}
                variant="outlined"
                placeholder="Vizyonumuz"
                helperText={t('ecommerceServiceSettings.customTabs.tabNameHelper')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.customTabs.pageTitle')}
                value={tab.title}
                onChange={(e) => updateTab(tab.id, 'title', e.target.value)}
                variant="outlined"
                placeholder="Vizyonumuz ve Hedeflerimiz"
                helperText={t('ecommerceServiceSettings.customTabs.pageTitleHelper')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={tab.backgroundColor}
                onChange={(e) => updateTab(tab.id, 'backgroundColor', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={tab.backgroundColor}
                        onChange={(e) => updateTab(tab.id, 'backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">{t('ecommerceAboutSettings.customTabs.status')}</Typography>
                <Button
                  variant={tab.active ? "contained" : "outlined"}
                  color={tab.active ? "success" : "warning"}
                  onClick={() => toggleTabActive(tab.id)}
                  startIcon={tab.active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                >
                  {tab.active ? t('ecommerceAboutSettings.customTabs.active') : t('ecommerceAboutSettings.customTabs.hidden')}
                </Button>
              </Box>
            </Grid>
            {/* Ana İçerik - HTML Editörü */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {t('ecommerceAboutSettings.customTabs.mainTabContent')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('ecommerceServiceSettings.customTabs.mainTabContentHelper')}
              </Typography>
              <HTMLEditor
                value={tab.content || ''}
                onChange={(value) => updateTab(tab.id, 'content', value)}
                placeholder={t('ecommerceServiceSettings.customTabs.mainTabContentPlaceholder')}
                companyId={companyId}
              />
            </Grid>

            {/* Dinamik Bölümler */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <SortableCustomTabSections
                sections={tab.sections || []}
                onAdd={() => addNewSection(tab.id)}
                onDelete={(sectionId) => deleteSection(tab.id, sectionId)}
                onUpdate={(sectionId, field, value) => updateSection(tab.id, sectionId, field, value)}
                onReorder={(nextSections) => reorderSections(tab.id, nextSections)}
                titlePlaceholder={t('ecommerceServiceSettings.customTabs.sectionTitlePlaceholder')}
                contentPlaceholder={t('ecommerceServiceSettings.customTabs.sectionContentPlaceholder')}
                emptyMessage={t('ecommerceServiceSettings.customTabs.noSections')}
              />
            </Grid>
          </Grid>

          {/* Durum Göstergesi */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: tab.active ? '#e8f5e8' : '#fff3cd', borderRadius: 1 }}>
            <Typography variant="body2" color={tab.active ? 'success.main' : 'warning.main'}>
              {tab.active ? t('ecommerceServiceSettings.customTabs.activeInfo') : t('ecommerceServiceSettings.customTabs.hiddenInfo')}
            </Typography>
          </Box>
        </TabPanel>
      ))}

      {/* Yeni Sekme Ekleme Modal'ı */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('ecommerceServiceSettings.modals.addNewTabTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('ecommerceServiceSettings.modals.addNewTabDescription')}
          </DialogContentText>
          <NewTabInput 
            onAddTab={(tabName) => {
              handleAddTab(tabName);
              setIsModalOpen(false);
            }}
            onCancel={() => setIsModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Sekme Silme Onay Modal'ı */}
      <Dialog
        open={deleteModalOpen}
        onClose={!deleting ? cancelDeleteTab : undefined}
        aria-labelledby="delete-tab-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-tab-dialog-title">
          {t('ecommerceAboutSettings.modals.deleteTabTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ecommerceServiceSettings.modals.deleteTabConfirm', { tabName: deleteTabName })}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'warning.main', fontWeight: 'medium' }}>
            {t('ecommerceAboutSettings.modals.deleteTabWarning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={cancelDeleteTab} 
            disabled={deleting}
            color="primary"
          >
            {t('ecommerceAboutSettings.actions.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteTab} 
            disabled={deleting}
            color="error"
            variant="contained"
            autoFocus
          >
            {deleting ? t('ecommerceAboutSettings.actions.deleting') : t('ecommerceAboutSettings.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* İkon Seçici Modal */}
      <IconSelector
        selectedIcon={selectedValueIndex >= 0 ? settings.valuesSection.values[selectedValueIndex]?.icon || '' : ''}
        onIconSelect={handleIconSelect}
        open={iconSelectorOpen}
        onClose={() => {
          setIconSelectorOpen(false);
          setSelectedValueIndex(-1);
        }}
      />

      <PagePickerDialog
        open={pagePickerOpen}
        configId={configId}
        onClose={() => {
          setPagePickerOpen(false);
          setPagePickerTargetId(null);
        }}
        onSelect={handlePagePickerSelect}
      />
    </Paper>
  );
};

export default ServiceSettingsComponent;
